/* SPAN i18n — minimal scaffold for EN/HI/BN/AR.
   Translations cover nav labels and key public-facing CTAs only.
   Add new keys as needed. */
import { createContext, useContext, useEffect, useState } from "react";

const LANGS = {
  en: { code: "en", label: "English", dir: "ltr" },
  hi: { code: "hi", label: "हिन्दी", dir: "ltr" },
  bn: { code: "bn", label: "বাংলা", dir: "ltr" },
  ar: { code: "ar", label: "العربية", dir: "rtl" },
};

const T = {
  en: {
    "nav.dashboard": "Dashboard", "nav.cases": "Cases", "nav.clients": "Clients",
    "nav.invoices": "Invoices", "nav.payables": "Payables", "nav.escalations": "Escalations",
    "nav.leads": "Leads", "nav.fieldOps": "Field Ops", "nav.intelligence": "Intelligence",
    "nav.network": "Network", "nav.quality": "Quality", "nav.analytics": "Analytics",
    "nav.billing": "Billing", "nav.developer": "Developer", "nav.audit": "Audit log",
    "nav.settings": "Settings", "nav.products": "Products", "nav.parties": "Parties",
    "report.title": "Report a counterfeit or IP infringement",
    "report.subtitle": "Tell us what you saw. SPAN treats every report as a confidential case file.",
    "report.submit": "Submit report",
    "report.success": "Thank you. Your report is now case",
    "common.cancel": "Cancel", "common.save": "Save", "common.delete": "Delete",
    "common.add": "Add", "common.search": "Search…",
  },
  hi: {
    "nav.dashboard": "डैशबोर्ड", "nav.cases": "केस", "nav.clients": "क्लाइंट",
    "nav.invoices": "इनवॉइस", "nav.payables": "देयक", "nav.escalations": "एस्केलेशन",
    "nav.leads": "लीड्स", "nav.fieldOps": "फील्ड ऑप्स", "nav.intelligence": "इंटेलिजेंस",
    "nav.network": "नेटवर्क", "nav.quality": "क्वालिटी", "nav.analytics": "एनालिटिक्स",
    "nav.billing": "बिलिंग", "nav.developer": "डेवलपर", "nav.audit": "ऑडिट लॉग",
    "nav.settings": "सेटिंग्स", "nav.products": "उत्पाद", "nav.parties": "पार्टियाँ",
    "report.title": "नकली उत्पाद या IP उल्लंघन की रिपोर्ट करें",
    "report.subtitle": "हमें बताएं कि आपने क्या देखा। SPAN हर रिपोर्ट को गोपनीय केस फ़ाइल मानता है।",
    "report.submit": "रिपोर्ट भेजें",
    "report.success": "धन्यवाद। आपकी रिपोर्ट अब केस है",
    "common.cancel": "रद्द करें", "common.save": "सहेजें", "common.delete": "हटाएँ",
    "common.add": "जोड़ें", "common.search": "खोजें…",
  },
  bn: {
    "nav.dashboard": "ড্যাশবোর্ড", "nav.cases": "মামলা", "nav.clients": "ক্লায়েন্ট",
    "nav.invoices": "চালান", "nav.payables": "প্রদেয়", "nav.escalations": "এসকেলেশন",
    "nav.leads": "লিড", "nav.fieldOps": "ফিল্ড অপস", "nav.intelligence": "ইন্টেলিজেন্স",
    "nav.network": "নেটওয়ার্ক", "nav.quality": "গুণমান", "nav.analytics": "অ্যানালিটিক্স",
    "nav.billing": "বিলিং", "nav.developer": "ডেভেলপার", "nav.audit": "অডিট লগ",
    "nav.settings": "সেটিংস", "nav.products": "পণ্য", "nav.parties": "পক্ষ",
    "report.title": "নকল পণ্য বা IP লঙ্ঘনের রিপোর্ট করুন",
    "report.subtitle": "আপনি কী দেখেছেন বলুন। SPAN প্রতিটি রিপোর্টকে গোপনীয় কেস ফাইল হিসেবে গণ্য করে।",
    "report.submit": "রিপোর্ট জমা দিন",
    "report.success": "ধন্যবাদ। আপনার রিপোর্ট এখন কেস",
    "common.cancel": "বাতিল", "common.save": "সংরক্ষণ", "common.delete": "মুছুন",
    "common.add": "যোগ করুন", "common.search": "অনুসন্ধান…",
  },
  ar: {
    "nav.dashboard": "لوحة القيادة", "nav.cases": "القضايا", "nav.clients": "العملاء",
    "nav.invoices": "الفواتير", "nav.payables": "الذمم الدائنة", "nav.escalations": "التصعيدات",
    "nav.leads": "العملاء المحتملون", "nav.fieldOps": "العمليات الميدانية", "nav.intelligence": "الاستخبارات",
    "nav.network": "الشبكة", "nav.quality": "الجودة", "nav.analytics": "التحليلات",
    "nav.billing": "الفوترة", "nav.developer": "المطور", "nav.audit": "سجل التدقيق",
    "nav.settings": "الإعدادات", "nav.products": "المنتجات", "nav.parties": "الأطراف",
    "report.title": "أبلغ عن تقليد أو انتهاك للملكية الفكرية",
    "report.subtitle": "أخبرنا بما رأيته. تتعامل SPAN مع كل تقرير كملف قضية سري.",
    "report.submit": "إرسال التقرير",
    "report.success": "شكراً لك. تقريرك الآن قضية",
    "common.cancel": "إلغاء", "common.save": "حفظ", "common.delete": "حذف",
    "common.add": "إضافة", "common.search": "بحث…",
  },
};

const I18nContext = createContext({ lang: "en", t: (k) => k, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("span_lang") || "en");
  useEffect(() => {
    localStorage.setItem("span_lang", lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = LANGS[lang]?.dir || "ltr";
  }, [lang]);
  const t = (key) => (T[lang] && T[lang][key]) || T.en[key] || key;
  return (
    <I18nContext.Provider value={{ lang, setLang, t, langs: LANGS }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => useContext(I18nContext);
export { LANGS };
